import { useCallback, useEffect, useState } from 'react';
import { proxyConfig } from '../../configs/proxyConfig';
import { correctExternalUrl, getAppUrl, getCsrfToken, fetchWithTargetApi, getUniversFromUrl } from 'project/helpers';
import type { SSOItem } from 'store/univers/universModels';
import { logout as logoutStroe } from 'store/componentsSlice';
import { useAppDispatch } from 'store/hooks';
import { APP_URL, LOCAL_STORAGE_KEYS } from 'project/defines';
import { getLocalStorageItem } from 'utils/helpers/localStorage';
import { env } from "env";
import { clearUserData } from 'store/auth/authSlice';



interface UseAuthReturn {
    isAuthenticated: boolean;
    user: any;
    isLoading: boolean;
    buildAuthUrl: (sso: SSOItem, univers?: string) => string;
    checkAuth: () => Promise<void>;
    checkUser: (state: string) => Promise<void>;
    logout: () => Promise<void>;
}

export const useAuth = (): UseAuthReturn => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useAppDispatch();

    // Fonction pour construire l'URL d'authentification dynamique
    const buildAuthUrl = useCallback((sso: SSOItem, univers?: string) => {
        const clientId: string = sso.wso_client_id ?? "";
        if (!univers) {
            univers = getUniversFromUrl('').replace("-fr", "")
        }
        const originalUri = `${globalThis.location.origin}/wso/callback?state=${univers};${sso.sso_code}`;

        // Construire le redirect_uri complet AVANT de l'encoder
        const redirectUri = `${proxyConfig.endpoints.login}?sso_code=${sso.sso_code}&originalUri=${originalUri}`;
        const authParams = new URLSearchParams({
        scope: 'openid',
        state: sso.sso_code, //'testjdome',
        response_type: 'code',
        client_id: clientId,
        redirect_uri: redirectUri,

        });

        const finalUrl = `${env.login_url}?${authParams.toString()}`;
        // console.log('🔍 URL d\'authentification générée:', {
        //   currentUrl,
        //   originalUri,
        //   redirectUri,
        //   authParams: Object.fromEntries(authParams),
        //   finalUrl
        // })

        return finalUrl;

    }, []);

    const logout = useCallback(async () => {
        try {
            if (!isAuthenticated) return;

            setIsLoading(true);
            
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };

            // Ajouter le header CSRF si disponible
            const csrfToken = getCsrfToken();
            if (csrfToken) {
                headers['X-CSRF-Token'] = csrfToken;
                //console.log('✅ Header CSRF ajouté:', csrfToken)
            } else {
                //console.log('❌ Aucun token CSRF trouvé - la requête va probablement échouer')
            }

            const logoutUrl = new URL(proxyConfig.endpoints.logout, globalThis.location.origin);
            logoutUrl.searchParams.set('postLogoutRedirectUri', globalThis.location.origin);

            


            const response = await fetchWithTargetApi(logoutUrl.toString(), {
                method: 'GET',
                credentials: 'include',
                headers
            });

            // Nettoyer les cookies côté client pour être sûr
            document.cookie = 'csrf=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure';
            document.cookie = 'csrfToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure';
            document.cookie = 'sid=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure';
            //console.log('🧹 Cookies nettoyés côté client')
            setIsAuthenticated(false);
            setUser(null);
            dispatch(clearUserData());
            dispatch(logoutStroe());



            if (response.status === 403) {
                const errorText = await response.text();

                if (errorText.includes('CSRF')) {
                    //console.log('🔐 Erreur CSRF détectée - le token était peut-être invalide')
                }
            }

            
            
            // Si succès (200/204) ou si on veut forcer le nettoyage local même en cas d'erreur
            if (response.ok) {
                //console.log('✅ Déconnexion réussie côté serveur')

                const data = await response.json();
                // Terminer la session SSO côté IdP en arrière-plan, sans quitter la page
                if (data.logoutUrl) {
                    globalThis.location.href = correctExternalUrl(data.logoutUrl);
                    // const iframe = document.createElement('iframe');
                    // iframe.style.display = 'none';
                    // iframe.addEventListener('load', () => iframe.remove(), { once: true });
                    // iframe.src = correctExternalUrl(data.logoutUrl);
                    // document.body.appendChild(iframe);
                    return;
                }
                globalThis.location.href = getAppUrl(APP_URL.HomePage);
                return;
                // Actualiser le statut après déconnexion
                // setProxyAuthStatus({
                //   isAuthenticated: false,
                //   user: null,
                //   isLoading: false
                // });

                // Optionnel: recharger la page pour nettoyer complètement l'état
                // window.location.reload();
            } else {
                //console.log('⚠️ Erreur côté serveur, mais nettoyage de l\'état local quand même')

                // Nettoyer l'état local même si le serveur a renvoyé une erreur
                // setProxyAuthStatus({
                //   isAuthenticated: false,
                //   user: null,
                //   isLoading: false
                // });
            }
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, setIsAuthenticated, setUser, dispatch]);

    const checkUser = useCallback(async (state: string) => {
        try {
            const univers = getUniversFromUrl('').replace("-fr", "")
            const data = await fetchWithTargetApi(`${proxyConfig.baseUrl}/api/wso/checkuser/${univers}?state=${state}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(response => response.json());
            return data;
        } catch (error) {
            console.error('Logout failed:', error);
        }
        return { message: "Unknown error" }
    }, [])

    const checkAuth = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetchWithTargetApi(proxyConfig.endpoints.status, {
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setIsAuthenticated(data.isAuthenticated);

                // Forcer la mise à jour du cookie CSRF côté client
                // Cela synchronise le cookie avec le token reçu du serveur
                if (data.csrfToken) {
                    document.cookie = `csrf=${data.csrfToken}; path=/; secure; samesite=strict`;
                } else {
                    // Nettoyer les cookies côté client pour être sûr
                    document.cookie = 'csrf=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure';
                    document.cookie = 'csrfToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure';
                    document.cookie = 'sid=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure';
                }
                setUser(data);

                const autData = getLocalStorageItem(LOCAL_STORAGE_KEYS.Auth);
                (isAuthenticated || autData) && !data.isAuthenticated && logout();
            } else if (response.status === 401) {
                setIsAuthenticated(false);
                setUser(null);
                logout();
            } else {
                throw new Error(`Unexpected status: ${response.status}`);
            }
        } catch (error) {
            // console.error('Auth check failed:', error);
            // setIsAuthenticated(false);
            // setUser(null);
            // logout();
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, setIsAuthenticated, setUser, logout]);

    

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    return {
        isAuthenticated,
        user,
        isLoading,
        buildAuthUrl,
        checkAuth,
        checkUser,
        logout,
    };
};
